import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ModalController } from '@ionic/angular';
import { DataService } from 'src/app/data.service';
import { ZaradekModalComponent } from 'src/app/components/zaradek-modal/zaradek-modal.component';
import { JogviszonyModalComponent } from 'src/app/components/jogviszony-modal/jogviszony-modal.component';
import { EntityPickerModalComponent } from 'src/app/components/entity-picker-modal/entity-picker-modal.component';

interface Osztaly {
  id: string;
  osztaly: string;
  ev?: number;
  evfolyam?: string | number;
  tanulo_db?: number;
}

interface TanuloOsztalySor {
  oktazon: string;
  adid?: string;
  full_name: string;
  aktualis_osztaly?: string;
  osztaly_id?: string;
  osztalyban: number | boolean;
  naplosorszam?: string | number | null;
  torzslapszam?: string | number | null;
  /** Beírási napló sorszáma – a tanulóé (tanévek között ugyanaz), a KRÉTÁ-ból is jöhet. */
  beirasi_naplo_sorszam?: string | null;
  selected?: boolean;
  /** Lezárult a jogviszonya (kiiratkozott) – az „archív" nézet ez alapján szűr. */
  kiiratkozott?: boolean;
}

@Component({
  selector: 'app-osztaly-besorolas',
  templateUrl: './osztaly-besorolas.component.html',
  styleUrls: ['./osztaly-besorolas.component.scss'],
})
export class OsztalyBesorolasComponent implements OnInit {
  sajatIntezmeny: any;
  osztalyok: Osztaly[] = [];
  tanulok: TanuloOsztalySor[] = [];
  szurtTanulok: TanuloOsztalySor[] = [];
  kivalasztottOsztaly: Osztaly | null = null;
  kereses = '';
  csakOsztalyTagok = true;
  /** Archív nézet: a lezárt jogviszonyú (kiiratkozott) tanulók is látszanak. */
  kiiratkozottakIs = false;
  /*  „ÁT AKARTAM JELENTKEZTETNI, ÉS ELTŰNT."
   *
   *  Aki kikerül egy osztályból, azt KÉT alapértelmezett szűrő rejti el egyszerre:
   *  a „csak osztálytagok", és – ha a jogviszonya is lezárult – a „kiiratkozottak is"
   *  kikapcsolt állapota. Így úgy tűnt, a tanuló eltűnt a rendszerből, holott ott volt.
   *  Ez a nézet pont őket hozza elő: akinek MOST NINCS osztálya. A másik két szűrő
   *  ilyenkor félreáll, különben pont azt rejtené el, amit meg akarunk találni. */
  csakOsztalyNelkuli = false;
  /** A kiiratkozottak oktatási azonosítói – ebből jelöljük a sorokat. */
  private kiiratkozottak = new Set<string>();
  betoltes = false;
  mentes = false;

  // Osztály adatainak módosítása (átnevezés, évfolyam, osztályfőnök)
  ujNev = '';
  ujEvfolyam: number | null = null;
  ujOf = '';
  nevMentes = false;

  // Új osztály létrehozása
  ujOsztalyForm = false;
  ujOsztaly: { osztaly: string; evfolyam: number | null; ev: number; of: string; szin: string } = {
    osztaly: '', evfolyam: null, ev: new Date().getFullYear(), of: '', szin: '',
  };
  osztalyLetrehozasFolyamatban = false;

  /*  OSZTÁLYFŐNÖK-HELYETTES(EK) – a kiválasztott osztályé. A helyettes ugyanazt teheti,
   *  mint az osztályfőnök (hiányzás igazolása, Osztályfőnök menü), de a bizonyítványon
   *  és a törzslapon továbbra is az osztályfőnök neve áll. */
  helyettesek: { allomany: string; nev: string }[] = [];
  helyettesMentes = false;
  private munkatarsak: any[] = [];

  /*  Tömeges betöltés: az iskola „osztály – ofő – helyettes" táblázatából kimásolt sorok. */
  listaNyitva = false;
  listaSzoveg = '';
  listaEredmeny: any = null;
  listaFut = false;

  constructor(private http: HttpClient, private data: DataService, private modalCtrl: ModalController) {}

  // Munkatanév: ha az admin egy előkészítés alatt álló tanéven dolgozik, az osztályok
  // és a besorolás ARRA az évre vonatkoznak (a backend ?ev= paraméterrel a tanév
  // kezdőnapjához viszonyítja a jogviszonyokat).
  munkatanev: number | null = null;
  aktualistanev: number | null = null;

  get elokeszitoMod(): boolean {
    return this.munkatanev != null && this.aktualistanev != null
      && Number(this.munkatanev) !== Number(this.aktualistanev);
  }

  /**
   *  ?ev=… lekérdezés-paraméter. MINDIG kiküldjük (nem csak előkészítő módban):
   *  az osztály tanévhez kötött adat, enélkül a lista minden év osztályát felsorolta,
   *  tehát a tavalyi osztályok is megjelentek az ideiben (és fordítva).
   */
  private get evParam(): string {
    return this.munkatanev != null ? `?ev=${this.munkatanev}` : '';
  }

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    this.munkatanev = await this.data.getMunkatanev();
    this.aktualistanev = await this.data.getAktualisTanev();
    this.osztalyokBetoltese();
  }

  /** A kiiratkozottak listája – a sorok jelöléséhez és a szűréshez. */
  private async kiiratkozottakBetoltese() {
    if (!this.intezmenyId) return;
    try {
      const sorok: any = await this.http
        .get(`/api/jogviszony/kiiratkozottak/${this.intezmenyId}`).toPromise();
      this.kiiratkozottak = new Set((sorok || []).map((s: any) => String(s.oktazon)));
      for (const t of this.tanulok as any[]) {
        t.kiiratkozott = this.kiiratkozottak.has(String(t.oktazon));
      }
      this.szures();
    } catch { /* a jelölés nélkül is használható a lista */ }
  }

  /**
   *  Kiiratkozás: a jogviszony lezárása záradékkal, egy tranzakcióban. A záradék
   *  kötelező – a szerver enélkül elutasítja –, ezért közös ablakban kérjük be.
   */
  async kiiratkozas(tanulo: any) {
    const modal = await this.modalCtrl.create({
      component: JogviszonyModalComponent,
      componentProps: {
        muvelet: 'kiiratkozas',
        oktazon: tanulo.oktazon,
        tanuloNev: tanulo.full_name,
        intezmenyId: this.intezmenyId,
      },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.ok) {
      tanulo.kiiratkozott = true;
      await this.kiiratkozottakBetoltese();
    }
  }

  async zaradekokMegnyitasa(tanulo: TanuloOsztalySor) {
    const modal = await this.modalCtrl.create({
      component: ZaradekModalComponent,
      componentProps: {
        oktazon: tanulo.oktazon,
        tanuloNev: tanulo.full_name,
        intezmenyId: this.intezmenyId,
      },
    });
    await modal.present();
  }

  get intezmenyId(): string {
    return String(this.sajatIntezmeny?.intezmenyId || this.sajatIntezmeny?.id || '').trim();
  }

  osztalyLetrehozas() {
    if (!this.intezmenyId) return;
    const nev = String(this.ujOsztaly.osztaly || '').trim();
    if (!nev) { this.data.sendToast('Adj meg osztálynevet.', 'warning'); return; }
    if (this.ujOsztaly.evfolyam == null || isNaN(Number(this.ujOsztaly.evfolyam))) {
      this.data.sendToast('Adj meg évfolyamot.', 'warning'); return;
    }
    this.osztalyLetrehozasFolyamatban = true;
    const ev = Number(this.munkatanev) || Number(this.ujOsztaly.ev) || new Date().getFullYear();
    this.http.post(`/api/tanuloi-osztaly/osztaly/${this.intezmenyId}`, {
      osztaly: nev,
      evfolyam: Number(this.ujOsztaly.evfolyam),
      ev,
      tanev: ev,
      of: this.ujOsztaly.of || null,
      szin: this.ujOsztaly.szin || null,
    }).subscribe({
      next: () => {
        this.osztalyLetrehozasFolyamatban = false;
        this.data.sendToast('Az osztály létrehozva.');
        this.ujOsztaly = { osztaly: '', evfolyam: null, ev, of: '', szin: '' };
        this.ujOsztalyForm = false;
        this.osztalyokBetoltese();
      },
      error: (err) => {
        this.osztalyLetrehozasFolyamatban = false;
        this.data.sendToast(err?.error?.error || 'Nem sikerült létrehozni az osztályt.', 'error');
      },
    });
  }

  osztalyokBetoltese() {
    if (!this.intezmenyId) return;
    this.betoltes = true;

    this.http.get<Osztaly[]>(`/api/tanuloi-osztaly/osztalyok/${this.intezmenyId}${this.evParam}`).subscribe({
      next: (rows) => {
        this.osztalyok = Array.isArray(rows) ? rows : [];
        this.betoltes = false;
      },
      error: () => {
        this.betoltes = false;
        this.data.sendToast('Nem sikerült betölteni az osztályokat.', 'error');
      },
    });
  }

  async osztalyPickerNyitas() {
    const modal = await this.modalCtrl.create({
      component: EntityPickerModalComponent,
      componentProps: {
        title: 'Osztály választása',
        searchPlaceholder: 'Osztály neve…',
        multiple: false,
        selectedIds: this.kivalasztottOsztaly ? [this.kivalasztottOsztaly.id] : [],
        items: this.osztalyok.map((o) => ({
          id: o.id,
          label: o.osztaly,
          sublabel: [
            o.ev ? `${o.ev}` : '',
            o.tanulo_db ? `${o.tanulo_db} tanuló` : '',
          ].filter(Boolean).join(' · ') || undefined,
        })),
      },
    });
    await modal.present();
    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm' && data?.ids?.length) {
      this.osztalyValasztas(data.ids[0]);
    }
  }

  osztalyValasztas(osztalyId: string) {
    this.kivalasztottOsztaly = this.osztalyok.find((osztaly) => osztaly.id === osztalyId) || null;
    this.tanulok = [];
    this.szurtTanulok = [];
    this.ujNev = (this.kivalasztottOsztaly as any)?.osztaly || '';
    this.ujEvfolyam = (this.kivalasztottOsztaly as any)?.evfolyam ?? null;
    this.ujOf = (this.kivalasztottOsztaly as any)?.of || '';
    this.helyettesek = [];
    if (!this.kivalasztottOsztaly) return;
    this.tanulokBetoltese();
    this.helyettesekBetoltese();
  }

  /* ── Osztályfőnök-helyettes ─────────────────────────────────────────────── */

  helyettesekBetoltese() {
    const id = this.kivalasztottOsztaly?.id;
    if (!id || !this.intezmenyId) return;
    this.http.get<any[]>(`/api/kinevezes/osztalyfonok-helyettes/${id}/${this.intezmenyId}`).subscribe({
      next: (sorok) => { if (this.kivalasztottOsztaly?.id === id) this.helyettesek = sorok || []; },
      // A helyettesek nélkül is használható a képernyő – csak ne mutassunk régit.
      error: () => { this.helyettesek = []; },
    });
  }

  /** A munkatársak listája a választóhoz – egyszer töltjük be. */
  private async munkatarsakBetoltese(): Promise<any[]> {
    if (!this.munkatarsak.length) {
      const sorok: any = await firstValueFrom(this.data.getAllomany(this.intezmenyId)).catch(() => []);
      this.munkatarsak = Array.isArray(sorok) ? sorok : [];
    }
    return this.munkatarsak;
  }

  async helyettesValasztas() {
    if (!this.kivalasztottOsztaly?.id) return;
    const munkatarsak = await this.munkatarsakBetoltese();
    const of = String(this.ujOf || '').toLowerCase();
    const modal = await this.modalCtrl.create({
      component: EntityPickerModalComponent,
      componentProps: {
        title: 'Osztályfőnök-helyettes(ek)',
        searchPlaceholder: 'Munkatárs neve…',
        multiple: true,
        selectedIds: this.helyettesek.map((h) => h.allomany),
        // Az osztályfőnök nem lehet a saját helyettese, ezért ki sem kínáljuk.
        items: munkatarsak
          .filter((m) => m?.adid && String(m.adid).toLowerCase() !== of)
          .map((m) => ({
            id: m.adid,
            label: [m.viselt_vez, m.viselt_ker].filter(Boolean).join(' ') || m.adid,
            sublabel: m.adid,
          })),
      },
    });
    await modal.present();
    const { data, role } = await modal.onWillDismiss();
    if (role === 'confirm' && Array.isArray(data?.ids)) this.helyettesekMentese(data.ids);
  }

  helyettesLevetele(allomany: string) {
    this.helyettesekMentese(this.helyettesek.map((h) => h.allomany).filter((a) => a !== allomany));
  }

  /** A TELJES listát küldjük: a kiszolgáló arra igazít (felvesz és levesz egyben). */
  private helyettesekMentese(lista: string[]) {
    const id = this.kivalasztottOsztaly?.id;
    if (!id) return;
    this.helyettesMentes = true;
    this.http.put<any>(`/api/kinevezes/osztalyfonok-helyettes/${id}`, {
      intezmeny: this.intezmenyId,
      helyettesek: lista,
    }).subscribe({
      next: (r) => {
        this.helyettesMentes = false;
        this.data.sendToast(r?.kihagyott_of
          ? 'Helyettesek mentve. Az osztályfőnököt nem vettük fel a saját helyettesének.'
          : 'Az osztályfőnök-helyettesek mentve.');
        this.helyettesekBetoltese();
      },
      error: (err) => {
        this.helyettesMentes = false;
        this.data.sendToast(err?.error?.error || 'A helyettesek mentése nem sikerült.', 'error');
      },
    });
  }

  /* ── Tömeges betöltés listából ──────────────────────────────────────────── */

  /** A lista tanéve: a munkatanév, ha van; különben a kiválasztott osztályé / az aktuális. */
  get listaTanev(): number {
    return Number(this.munkatanev) || Number((this.kivalasztottOsztaly as any)?.tanev)
      || Number(this.aktualistanev) || new Date().getFullYear();
  }

  /**
   *  Előbb MINDIG próba: a kiszolgáló megmutatja, melyik sort ismerte fel, és melyiknél
   *  nem egyértelmű a név. Csak ezután lehet betölteni – egy félreismert név jogot adna
   *  rossz embernek.
   */
  async listaFeldolgozas(proba: boolean) {
    if (!this.intezmenyId || !this.listaSzoveg.trim()) return;
    if (!proba && !confirm(`Betöltöd a(z) ${this.listaEredmeny?.rendben || 0} rendben lévő sort? `
      + 'Az érintett osztályok helyetteseit a lista szerint állítjuk be (üres oszlop = nincs helyettes).')) return;
    this.listaFut = true;
    try {
      await this.munkatarsakBetoltese(); // a felismert azonosítók mellé nevet írunk
      const r: any = await firstValueFrom(this.http.post('/api/kinevezes/osztalyfonok-lista', {
        ev: this.listaTanev,
        intezmeny: this.intezmenyId,
        szoveg: this.listaSzoveg,
        proba,
      }));
      this.listaEredmeny = r;
      if (!proba) {
        this.data.sendToast(`${r?.rendben || 0} osztály beállítva.`
          + (r?.uzenetek?.length ? ' ' + r.uzenetek.join(' ') : ''));
        this.osztalyokBetoltese();
        if (this.kivalasztottOsztaly) this.helyettesekBetoltese();
      }
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A lista feldolgozása nem sikerült.', 'error');
    } finally {
      this.listaFut = false;
    }
  }

  munkatarsNev(adid: string | null): string {
    if (!adid) return '';
    const m = this.munkatarsak.find((x) => String(x.adid) === String(adid));
    return m ? [m.viselt_vez, m.viselt_ker].filter(Boolean).join(' ') : adid;
  }

  /**
   *  Osztály adatainak mentése (átnevezés, évfolyam, osztályfőnök).
   *  Az osztályra mindenhol az AZONOSÍTÓ hivatkozik – a jogviszony, az órarend és a
   *  tantárgyfelosztás is –, ezért az átnevezés nem szakít el semmit. Csak az adott
   *  tanév osztálysorát írja: a korábbi tanévek külön sorok, azok érintetlenek.
   */
  osztalyAdatMentes() {
    if (!this.intezmenyId || !this.kivalasztottOsztaly?.id) return;
    const nev = String(this.ujNev || '').trim();
    if (!nev) { this.data.sendToast('Az osztály neve nem lehet üres.', 'warning'); return; }

    this.nevMentes = true;
    this.http
      .patch<any>(`/api/tanuloi-osztaly/osztaly/${this.kivalasztottOsztaly.id}/${this.intezmenyId}`, {
        osztaly: nev,
        evfolyam: this.ujEvfolyam,
        of: this.ujOf || null,
      })
      .subscribe({
        next: () => {
          this.nevMentes = false;
          const sor: any = this.osztalyok.find((o) => o.id === this.kivalasztottOsztaly?.id);
          if (sor) { sor.osztaly = nev; sor.evfolyam = this.ujEvfolyam; sor.of = this.ujOf || null; }
          if (this.kivalasztottOsztaly) {
            (this.kivalasztottOsztaly as any).osztaly = nev;
            (this.kivalasztottOsztaly as any).evfolyam = this.ujEvfolyam;
            (this.kivalasztottOsztaly as any).of = this.ujOf || null;
          }
          this.osztalyfonokKinevezes();
        },
        error: (err) => {
          this.nevMentes = false;
          this.data.sendToast(err?.error?.error || 'A mentés nem sikerült.', 'error');
        },
      });
  }

  /**
   *  Az osztályfőnök nem csak az `osztaly.of` mező: OF-kinevezés is tartozik hozzá,
   *  amiből a tantárgyfelosztás az óraszám-kedvezményt számolja. A két adat külön
   *  táblában él, ezért itt egy művelettel rendezzük mindkettőt – különben a
   *  kinevezés csak az admin Kinevezések menüjében, kézzel jönne létre.
   */
  private osztalyfonokKinevezes() {
    const ev = Number((this.kivalasztottOsztaly as any)?.ev)
      || Number(this.ujOsztaly?.ev) || new Date().getFullYear();

    this.http
      .put<any>(`/api/kinevezes/osztalyfonok/${this.kivalasztottOsztaly?.id}`, {
        of: this.ujOf || '',
        ev,
        intezmeny: this.intezmenyId,
      })
      .subscribe({
        next: (r) => {
          // Ha a helyettesből lett osztályfőnök, a kiszolgáló levette a helyettesek közül.
          this.helyettesekBetoltese();
          this.data.sendToast(r?.uzenet
            ? `Az osztály adatai mentve. ${r.uzenet}`
            : 'Az osztály adatai és az osztályfőnöki kinevezés mentve.');
        },
        // A kinevezés hibája nem teszi semmissé az osztály mentését – azt már elmentettük.
        error: (err) => this.data.sendToast(
          'Az osztály adatai mentve, de az osztályfőnöki kinevezés nem: '
          + (err?.error?.error || 'ismeretlen hiba'), 'warning'),
      });
  }

  /**
   *  Osztály törlése. A backend csak ÜRES osztályt enged törölni: a lezárt jogviszony
   *  is akadály, mert azon keresztül látszik egy korábbi tanév névsora.
   */
  osztalyTorles() {
    const o: any = this.kivalasztottOsztaly;
    if (!o?.id) return;
    if (!confirm(`Törlöd a(z) „${o.osztaly}" osztályt? Csak akkor sikerül, ha üres.`)) return;

    this.http.delete<any>(`/api/tanuloi-osztaly/osztaly/${o.id}/${this.intezmenyId}`).subscribe({
      next: () => {
        this.data.sendToast(`A(z) „${o.osztaly}" osztály törölve.`);
        this.kivalasztottOsztaly = null;
        this.tanulok = [];
        this.szurtTanulok = [];
        this.osztalyokBetoltese();
      },
      error: (err) => this.data.sendToast(err?.error?.error || 'A törlés nem sikerült.', 'error'),
    });
  }

  tanulokBetoltese() {
    if (!this.intezmenyId || !this.kivalasztottOsztaly?.id) return;
    this.betoltes = true;

    this.http
      .get<TanuloOsztalySor[]>(
        `/api/tanuloi-osztaly/osztaly/${this.kivalasztottOsztaly.id}/tanulok/${this.intezmenyId}${this.evParam}`
      )
      .subscribe({
        next: (rows) => {
          this.tanulok = (Array.isArray(rows) ? rows : []).map((row) => ({
            ...row,
            selected: !!row.osztalyban,
            naplosorszam: row.naplosorszam ?? '',
            torzslapszam: row.torzslapszam ?? '',
            beirasi_naplo_sorszam: row.beirasi_naplo_sorszam ?? '',
            kiiratkozott: this.kiiratkozottak.has(String(row.oktazon)),
          }));
          this.szures();
          this.betoltes = false;
          this.kiiratkozottakBetoltese();
        },
        error: () => {
          this.betoltes = false;
          this.data.sendToast('Nem sikerült betölteni a tanulókat.', 'error');
        },
      });
  }

  /*  MIT REJT EL A SZŰRÉS?
   *
   *  Az üres lista önmagában nem mondja meg, hogy nincs ilyen tanuló, vagy csak nem
   *  látszik. Ezek a számlálók a lista alatt kiírják, hány embert rejt a két
   *  alapértelmezett szűrő – és egy kattintással elő is hozhatók. */
  get osztalyNelkuliDb(): number {
    return (this.tanulok || []).filter((t) => !t.osztaly_id).length;
  }
  get rejtettNemTag(): number {
    if (this.csakOsztalyNelkuli || !this.csakOsztalyTagok) return 0;
    return (this.tanulok || []).filter((t) => !t.selected).length;
  }
  get rejtettKiiratkozott(): number {
    if (this.csakOsztalyNelkuli || this.kiiratkozottakIs) return 0;
    return (this.tanulok || []).filter((t) => t.kiiratkozott
      && (!this.csakOsztalyTagok || t.selected)).length;
  }

  /** Egy kattintás: mutasd azokat, akiknek most nincs osztályuk. */
  osztalyNelkulieketMutat() {
    this.csakOsztalyNelkuli = true;
    this.kereses = '';
    this.szures();
  }

  mindenkitMutat() {
    this.csakOsztalyNelkuli = false;
    this.csakOsztalyTagok = false;
    this.kiiratkozottakIs = true;
    this.szures();
  }

  szures() {
    const term = this.normalize(this.kereses);
    this.szurtTanulok = this.tanulok.filter((tanulo) => {
      if (this.csakOsztalyNelkuli) {
        if (tanulo.osztaly_id) return false;
      } else {
        if (this.csakOsztalyTagok && !tanulo.selected) return false;
        // Az „archiválás" nem adatmozgatás: a lezárt jogviszonyú tanuló itt marad,
        // csak alapból nem listázzuk.
        if (!this.kiiratkozottakIs && tanulo.kiiratkozott) return false;
      }
      if (!term) return true;
      return [
        tanulo.full_name,
        tanulo.oktazon,
        tanulo.adid,
        tanulo.aktualis_osztaly,
        tanulo.naplosorszam,
        tanulo.torzslapszam,
        tanulo.beirasi_naplo_sorszam,
      ].some((value) => this.normalize(value).includes(term));
    });
  }

  osszesLathatoKijelolese(selected: boolean) {
    for (const tanulo of this.szurtTanulok) {
      tanulo.selected = selected;
    }
  }

  mentesInditasa() {
    if (!this.intezmenyId || !this.kivalasztottOsztaly?.id) return;

    const tanulok = this.tanulok
      .filter((tanulo) => tanulo.selected)
      .map((tanulo) => ({
        oktazon: tanulo.oktazon,
        naplosorszam: tanulo.naplosorszam,
        torzslapszam: tanulo.torzslapszam,
        beirasi_naplo_sorszam: tanulo.beirasi_naplo_sorszam ?? '',
      }));

    this.mentes = true;
    this.http
      .put(`/api/tanuloi-osztaly/osztaly/${this.kivalasztottOsztaly.id}/tanulok/${this.intezmenyId}${this.evParam}`, {
        tanulok,
      })
      .subscribe({
        next: () => {
          this.mentes = false;
          this.data.sendToast('Az osztálynévsor mentve.');
          this.osztalyokBetoltese();
          this.tanulokBetoltese();
        },
        error: () => {
          this.mentes = false;
          this.data.sendToast('Nem sikerült menteni az osztálynévsort.', 'error');
        },
      });
  }

  get kijeloltDb(): number {
    return this.tanulok.filter((tanulo) => tanulo.selected).length;
  }

  private normalize(value: any): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}
